/*
Usage: 
  <skio-cart-upsell item='{{ item | json | escape }}' line-key="{{ item.key }}" line="{{ forloop.index }}"></skio-cart-upsell>
  <script src="{{ 'skio-cart-upsell.js' | asset_url }}" type="module"></script>
  
  May want to include above module globally if element is used in cart drawer
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js'

const skioCartStyles = css`
  .skio-cart-upsell {
    margin-top: 5px;
    width: 100%;
    padding:7px 5px;
    background: transparent;
    border: 1px solid #000; 
    border-radius: 10px;
    cursor: pointer;
    max-width: 300px;
    color: #000;
  }

  .skio-cart-upgrade {
    margin-top: 10px;
    background: #E0D7D1;
    color: #000;
    border: 1px solid #E0D7D1;
    outline: none;
    cursor: pointer;
    padding: 7px 5px;
    width: 100%;
    border-radius: 30px;
    transition: color 0.25s, background-color 0.25s, border-color 0.25s;
  }

  .skio-cart-upgrade:hover {
    color: #000;
    border-color: #000; 
    background: transparent;
  }
  .small {
    font-size: 14px; 
    margin-top: 10px;
  }  
`


export class SkioCartUpsell extends LitElement {
  static styles = skioCartStyles;

  static properties = {
    item: { type: Object },
    line: { type: Number },
    lineKey: { type: String, attribute: 'line-key' },

    product: { type: Object },
    selectedVariant: { type: Object },
    key: { type: String },
    
    skioSellingPlanGroups: {},
    availableSellingPlanGroups: {},

    selectedSellingPlanGroup: {},
    selectedSellingPlan: {},

    discount_format: { type: String },

    moneyFormatter: {},

    currency: { type: String }
  };

  constructor() {
    super();

    this.item = null;
    this.line = null;
    this.lineKey = null;
    this._pendingPlanUpdate = false;
    this._isUpdatingLineItem = false;

    this.product = null;
    this.selectedVariant = null;

    this.skioSellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.discountFormat = 'percent';

    this.currency = 'USD';
    this.moneyFormatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: this.currency,
    });
  }

  upgradeButton = () => {
    if (this.availableSellingPlanGroups?.length > 0) {
      return html`
      <button class="skio-cart-upgrade" type="button" @click=${() => this.selectSellingPlan(this.availableSellingPlanGroups[0].selected_selling_plan.id) }>Upgrade to subscription</button>
    `;
    } else { 
      return html``;
    }
  }

  changeFrequency = () => {
    return html`
      <label class="small">Subscription</label>
      <select class="skio-cart-upsell" skio-cart-upsell="${ this.key }" @change=${ (e) => this.selectSellingPlan(e.target.value) }>
        ${ !this.product.requires_selling_plan ? 
          html`
          <optgroup label="One Time Purchase">
            <option value="">One-time</option>
          </optgroup>
          `
        : ''}

        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) => 
          html`
            <optgroup label="${ group.name } (Save ${ this.discount(group.selected_selling_plan).percent })">
              ${ group ? group.selling_plans.map((selling_plan) => 
                html`
                <option value="${ selling_plan.id }" .selected=${ selling_plan.id == this.selectedSellingPlan?.id }>
                  ${ selling_plan.name }
                </option>
                `
              ): ''}
            </optgroup>
          `
        ): ''}
      </select>
    `;
  }

  render() {
    if(!this.item || !this.product || !this.selectedVariant) return;
    return this.selectedSellingPlan ? this.changeFrequency() : this.upgradeButton();
  }

  updated = (changed) => {

    if(changed.has('item') && this.item) {
      if (typeof this.item === 'string') {
        try {
          this.item = JSON.parse(this.item);
        } catch (error) {
          this.error('Could not parse line item JSON', error);
          return;
        }
      }
      this.fetchProduct(this.item.handle);
    }

    if(changed.has('product') && this.product) {

      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO'
      )

      this.selectedVariant = this.product.variants.find(variant => variant.id == this.item.variant_id);

      if(this.item.selling_plan_allocation) {
        this.selectedSellingPlanGroup = this.skioSellingPlanGroups.find(group => group.selling_plans.find(selling_plan => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id));
        this.selectedSellingPlan = this.selectedSellingPlanGroup?.selling_plans.find(selling_plan => selling_plan.id == this.item.selling_plan_allocation.selling_plan.id);
      }

    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      if (this.availableSellingPlanGroups.length) {
        this.availableSellingPlanGroups.forEach((group => {
          group.selected_selling_plan = group.selling_plans[0];
        }));
      }
    }

    if (changed.has('selectedSellingPlan') && this.product && this._pendingPlanUpdate) {
      this._pendingPlanUpdate = false;
      this.updateLineItem();
    }

  }

  log = (...args) => {
    args.unshift('%c[skio cart upsell]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c [skio cart upsell]', 'color: #ff0000');
    console.error.apply(console, args);
  }

  _resolveLineIdentifier(changeBody) {
    const lineKey = this.item?.key
      || this.lineKey
      || this.closest('line-item')?.querySelector('[data-line-key]')?.getAttribute('data-line-key');

    if (lineKey) {
      changeBody.id = lineKey;
      return true;
    }

    const lineIndex = parseInt(this.line, 10);
    if (Number.isInteger(lineIndex) && lineIndex > 0) {
      changeBody.line = lineIndex;
      return true;
    }

    return false;
  }

  _collectSectionsToBundle() {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    document.querySelectorAll('cart-drawer, cart-page').forEach((element) => {
      const section = element.closest('.shopify-section');
      if (!section?.id) {
        return;
      }
      const sectionId = section.id.replace('shopify-section-', '');
      if (!sectionsToBundle.includes(sectionId)) {
        sectionsToBundle.push(sectionId);
      }
    });

    return sectionsToBundle;
  }

  async updateLineItem() {
    if (!this.item || this._isUpdatingLineItem) return;

    const currentPlanId = this.item.selling_plan_allocation?.selling_plan?.id ?? null;
    const nextPlanId = this.selectedSellingPlan?.id ?? null;
    if (currentPlanId == nextPlanId) return;

    const changeBody = {
      quantity: this.item.quantity,
      selling_plan: nextPlanId
    };

    if (!this._resolveLineIdentifier(changeBody)) {
      this.error('Missing line item key or line index');
      return;
    }

    const sectionsToBundle = this._collectSectionsToBundle();
    if (sectionsToBundle.length > 0) {
      changeBody.sections = sectionsToBundle;
    }
    changeBody.sections_url = `${window.location.pathname}${window.location.search}`;

    this._isUpdatingLineItem = true;

    try {
      const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(changeBody)
      });

      const cartContent = await response.json();

      if (!response.ok || cartContent.status === 'bad_request') {
        this.error('Cart change failed', cartContent);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }

      const updatedItem = cartContent.items?.find((item) => item.key === changeBody.id)
        || cartContent.items?.[changeBody.line - 1];

      if (updatedItem) {
        this.item = updatedItem;
        if (updatedItem.key) {
          this.lineKey = updatedItem.key;
        }
      }

      if (cartContent.sections && Object.keys(cartContent.sections).length > 0) {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'line-item:change',
            cart: cartContent
          }
        }));
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } finally {
      this._isUpdatingLineItem = false;
    }
  }
  
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;
  }

  selectSellingPlan(id) {
    if(!id) {
      this._pendingPlanUpdate = true;
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
      return;
    }

    let group = this.availableSellingPlanGroups.find(group => group.selling_plans.find(selling_plan => selling_plan.id == id));
    let selling_plan = group?.selling_plans.find(x => x.id == id);
    
    if (selling_plan) {
      this._pendingPlanUpdate = true;
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    } else {
      this.log("Error: couldn't find selling plan with id " + id + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
    }
  }

  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  discount(selling_plan) {
    if (!selling_plan)
      return { percent: '0%', amount: 0 }
    
    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price;
    
    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round(
          (price * price_adjustment.value) / 100.0
        )
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round(
          (((price - price_adjustment.value) * 1.0) /
            price) *
            100.0
        )}%`
        discount.amount = price - price_adjustment.value
        break
    }
    
    return discount
  }

  fetchProduct = async(handle) => {
    let productCache = window.sessionStorage.skioCartProductCache ? JSON.parse(window.sessionStorage.skioCartProductCache) : [];
    let cachedProduct = productCache ? productCache.find(product => product.handle == handle) : null;
    
    if(cachedProduct) {
      this.product = cachedProduct;
    } else {
      await fetch(`/products/${ handle }.js`)
      .then((response) => response.json())
      .then((response) => {
        this.product = response;
        productCache.push(response);
        window.sessionStorage.skioCartProductCache = JSON.stringify(productCache);
        this.requestUpdate();
      })
    }
  }
}

customElements.define('skio-cart-upsell', SkioCartUpsell);
